/**
 * RetentionPeriodBadge - Badge de "Período" das solicitações de Retenção
 *
 * Exibe a faixa de tempo consumida desde a data de solicitação (24h, 48h, 72h,
 * 7 dias, 10 dias, 1 mês, 3 meses, 6 meses, 1 ano, Mais de 1 ano). Usado na
 * listagem e nos cards do Kanban (Retenção e Jurídico).
 *
 * @version 1.4.0
 * RELEASE: 2026-08-22 v1.4.0 - Faixas estendidas (3m/6m/1 ano/Mais de 1 ano)
 *          herdadas de `calcRetentionPeriod`
 * RELEASE: 2026-08-22 v1.3.0 - `borderless` passa a ser o padrão (sem cor de
 *          borda) em todas as telas de Retenção (listagem, kanban, dashboard)
 * RELEASE: 2026-08-22 v1.2.0 - Nova prop `borderless` (remove a linha colorida
 *          da borda, padrão usado nos cards do dashboard)
 * RELEASE: 2026-08-22 v1.1.0 - Badge mais compacto (texto mais próximo das bordas)
 * RELEASE: 2026-08-22 v1.0.0 - Criação inicial
 */
import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { calcRetentionPeriod } from '@/lib/tasks/retention-period';

interface RetentionPeriodBadgeProps {
  /** Data da solicitação (task_retention_details.requested_at) */
  requestedAt: string | null | undefined;
  /** Data de conclusão/cancelamento — quando ausente, calcula até agora */
  closedAt?: string | null;
  /** Versão compacta (usada nos cards do Kanban) */
  compact?: boolean;
  /** v1.3.0: remove a borda colorida (padrão true — mantém apenas fundo/texto) */
  borderless?: boolean;
  className?: string;
}

export function RetentionPeriodBadge({
  requestedAt,
  closedAt,
  compact = false,
  borderless = true,
  className,
}: RetentionPeriodBadgeProps) {
  const period = calcRetentionPeriod(requestedAt, closedAt);
  if (!period) return null;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            // v1.1.0: padding reduzido — o texto fica colado ao ícone/bordas
            'inline-flex items-center gap-0.5 rounded-md font-medium whitespace-nowrap leading-tight',
            borderless ? 'border-0' : 'border',
            compact ? 'px-1 py-0 text-[10px]' : 'px-1 py-0 text-[11px]',
            period.className,
            // v1.2.0: neutraliza a cor de borda vinda do preset
            borderless && 'border-transparent',
            className,
          )}
        >
          <Clock className={compact ? 'h-2.5 w-2.5' : 'h-2.5 w-2.5'} />
          {period.label}
        </span>
      </TooltipTrigger>
      <TooltipContent>{period.tooltip}</TooltipContent>
    </Tooltip>
  );
}

